import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../utils.js';
import { textVariants } from '../Text.js';

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-full  ring-offset-white transition-colors duration-300 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        gray:
          'bg-gray-50 hover:bg-gray-100 active:bg-gray-200 disabled:opacity-40 ' +
          textVariants({ font: 'medium-sm' }),
        primary:
          'bg-primary-orange hover:bg-orange-600 active:bg-orange-900 disabled:opacity-40 ' +
          textVariants({ font: 'medium-sm', color: 'white' }),
        secondary:
          'bg-gray-200 hover:bg-gray-300 active:bg-gray-400 disabled:opacity-20 ' +
          textVariants({ font: 'medium-sm', color: 'gray-500' }),
        black:
          'bg-black hover:bg-gray-800 active:bg-gray-900 disabled:opacity-20 ' +
          textVariants({ font: 'medium-sm', color: 'white' }),
        white:
          'bg-white hover:bg-gray-50 active:bg-gray-100 disabled:opacity-20 ' +
          textVariants({ font: 'medium-sm', color: 'black' }),
        green:
          'bg-green-800 hover:bg-green-600 active:bg-green-900 active:bg-blend-darken disabled:opacity-20 ' +
          textVariants({ font: 'medium-sm', color: 'white' }),
        'green-pro-paywall':
          'bg-[#00A203] hover:bg-[#008B03] active:bg-[#00A203] active:bg-blend-darken disabled:bg-[#5E5E5E] ' +
          textVariants({ font: 'medium-sm', color: 'white' }),
        icon:
          'bg-gray-200 hover:bg-gray-300 active:bg-gray-400 disabled:opacity-20 ' +
          textVariants({ font: 'medium-sm', color: 'black' }),
        'icon-white':
          'bg-white hover:bg-gray-50 active:bg-gray-100 disabled:opacity-20 ' +
          textVariants({ font: 'medium-sm', color: 'black' }),
        unstyled: 'bg-transparent hover:none',
        outline:
          'bg-white hover:bg-gray-50 active:bg-gray-100 disabled:opacity-20 border border-gray-200' +
          textVariants({ font: 'md', color: 'black' }),
        spinnerPrimary:
          'rounded-full bg-spinnerPrimary shadow-lg transition-transform focus:outline-none' +
          textVariants({
            font: 'medium-lg',
            color: 'white',
            family: 'arialRounded',
          }),
        spinnerSecondary:
          'rounded-full bg-spinnerSecondary shadow-lg focus:outline-none' +
          textVariants({
            font: 'medium-lg',
            color: 'white',
            family: 'arialRounded',
          }),
        mysteryCityPrimary:
          'rounded-[200px] border-[1px] disabled:opacity-50 border-[rgba(0,240,255,0.8)] text-[#00f0ff] bg-[radial-gradient(50%_50%_at_50%_50%,rgba(0,0,0,0)_0%,rgba(0,240,255,0.2)_100%),radial-gradient(50%_50%_at_50%_50%,rgba(255,255,255,0)_0%,rgba(0,255,224,0.2)_100%),radial-gradient(50%_50%_at_50%_50%,rgba(0,240,255,0.2)_0%,rgba(0,0,0,0)_100%),linear-gradient(180deg,rgba(217,217,217,0.8)_0%,rgba(217,217,217,0)_100%)] hover:border-[rgba(128,230,237,0.8)] hover:text-[#98EDF2] hover:bg-[radial-gradient(50%_50%_at_50%_50%,rgba(239,254,255,0)_0%,rgba(241,251,252,0.2)_100%),radial-gradient(50%_50%_at_50%_50%,rgba(255,255,255,0)_0%,rgba(0,255,224,0.2)_100%),radial-gradient(50%_50%_at_50%_50%,rgba(0,240,255,0.2)_0%,rgba(0,0,0,0)_100%),linear-gradient(180deg,rgba(217,217,217,0.8)_0%,rgba(217,217,217,0)_100%)] active:border-[rgba(6,179,190,0.8)] active:bg-[radial-gradient(50%_50%_at_50%_50%,rgba(0,240,255,0.15)_0%,rgba(13,93,98,0.2)_100%),radial-gradient(50%_50%_at_50%_50%,rgba(255,255,255,0)_0%,rgba(4,52,46,0.2)_100%),radial-gradient(50%_50%_at_50%_50%,rgba(0,240,255,0.2)_0%,rgba(0,0,0,0)_100%),linear-gradient(180deg,rgba(25,101,117,0.8)_0%,rgba(217,217,217,0)_100%)] active:text-[#001112] ' +
          textVariants({ font: 'medium-sm', color: 'aqua', family: 'space' }),
        mysteryCitySecondary:
          'rounded-[200px] border-[1px] disabled:opacity-50 text-[#00f0ff] bg-[#24414ECC]  hover:bg-[#24414E9F] active:bg-[#24414E] ' +
          textVariants({ font: 'medium-sm', color: 'aqua', family: 'space' }),
        mysteryCityError:
          'rounded-[200px] border-[1px] disabled:opacity-50 border-[#F08C8C]  bg-[#e61e1ecc] hover:bg-[#e61e1e9f] active:bg-[#e61e1e4e] ' +
          textVariants({
            font: 'medium-sm',
            color: 'mysteryCityRed',
            family: 'space',
          }),
        squashPrimary:
          'bg-blue-100 hover:bg-blue-200 active:bg-blue-600 disabled:opacity-40 ' +
          textVariants({ font: 'medium-sm', color: 'black' }),
        squashMint:
          'bg-[#96E782] hover:bg-[#87CF75] active:bg-[#4B7341] disabled:opacity-40 shadow-[0px_10px_10px_0px_rgba(0,0,0,0.70)]' +
          textVariants({ font: 'medium-sm', color: 'black' }),
      },
      size: {
        icon: 'h-10 w-10',
        sm: 'h-8 rounded-full px-2',
        md: 'h-10 rounded-full px-3 py-2',
        lg: 'h-12 rounded-full px-8 py-3',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'lg',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  fullWidth?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, fullWidth, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(
          buttonVariants({ className, variant, size }),
          fullWidth ? 'w-full' : ''
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
